import { useEffect, useMemo, useState } from 'react';
import { backdropClose } from '../../utils/backdropClose';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { formatWhatsAppText } from '../notifications/WhatsAppPreview';
import {
  Radar,
  Sparkles,
  PhoneCall,
  MessageCircle,
  Gift,
  SkipForward,
  UserX,
  Check,
  CheckCheck,
  Trophy,
  Send,
  ChevronRight,
  ChevronLeft,
  X,
  Ban,
  Clock,
  IndianRupee,
  Footprints,
  RefreshCw,
  Eye,
  Lock,
  Pencil,
  History
} from 'lucide-react';

/**
 * The Reach AI queue — one shared view for both surfaces:
 *  - gym managers at /reach (full actions, persisted daily unlock)
 *  - super admins inside a gym's detail page (readOnly: ephemeral preview,
 *    every flow browsable but nothing can be sent or persisted)
 * All data access is injected via `api` so each surface brings its own
 * endpoints/auth; UI changes here automatically reach both.
 */

// ── Types (mirror API DTOs) ───────────────────────────────────────────

interface FunnelInfo {
  state: 'read' | 'delivered' | 'sent' | 'failed' | 'none';
  sentAt: string | null;
  deliveredAt: string | null;
  seenAt: string | null;
}

export interface ReachEntry {
  memberId: string;
  rank: number;
  score: number;
  source: 'expired' | 'trial' | 'lead';
  reasons: string[];
  name: string;
  phone: string;
  optedOut: boolean;
  funnel: FunnelInfo;
  lastPlan: { name: string; price: number | null; endDate: string | null } | null;
  lifetimeSpend: number;
  membershipCount: number;
  lastVisitAt: string | null;
  snoozeUntil: string | null;
  callbackAt: string | null;
}

interface OverlayState {
  state?: 'open' | 'dropped' | 'won';
  snoozeUntil?: string | null;
  callbackAt?: string | null;
  droppedReason?: string | null;
  lastActionAt?: string | null;
  actionCount?: number;
}

export interface TodayData {
  exists: boolean;
  gymId: string;
  /** false when the gym's plan doesn't include the reach_ai feature. */
  featureEnabled?: boolean;
  preview?: boolean;
  generatedAt?: string;
  snapshotDate?: string;
  entries?: ReachEntry[];
  summary?: any;
  overlay?: Record<string, OverlayState>;
}

export interface MemberContext {
  member: { id: string; name: string; phone: string; status: string; optedOut: boolean; joinedAt: string | null };
  gymName: string;
  purchases: Array<{ id: string; planName: string; planPrice: number | null; amount: number; startDate: string | null; endDate: string | null; purchaseDate: string | null; status: string | null }>;
  totals: { lifetimeSpend: number; membershipCount: number; avgMonthlySpend: number; tenureMonths: number };
  lastVisitAt: string | null;
  messageHistory: Array<{ event: string; status: string; sentAt: string | null; deliveredAt: string | null; seenAt: string | null; at: string | null; preview: string | null }>;
  followUp: { state: string; snoozeUntil: string | null; callbackAt: string | null; actions: Array<{ type: string; payload: any; at: string | null }> } | null;
  capabilities: {
    resend: boolean;
    resendPreview: string | null;
    softReminder: boolean;
    softReminderPreview: string | null;
    offer: boolean;
    offerTemplateContent: string | null;
  };
}

/** Data access injected by the hosting surface (gym app vs super admin). */
export interface ReachHistoryEntry {
  id: string;
  type: string;
  at: string;
  memberId: string;
  memberName: string;
  memberPhone: string | null;
  performedBy: string | null;
  payload: any;
}

export interface ReachHistoryPage {
  total: number;
  hasMore: boolean;
  entries: ReachHistoryEntry[];
}

export interface ReachApi {
  getToday: () => Promise<TodayData>;
  /** Gym: persisted unlock. Super admin: ephemeral non-persisted preview. */
  unlock: () => Promise<TodayData>;
  getMemberContext: (memberId: string) => Promise<MemberContext>;
  /** Absent in readOnly mode — no mutations can even be attempted. */
  performAction?: (vars: { memberId: string; type: string; payload?: any }) => Promise<any>;
  /** Audit trail of actions taken from the queue — available on every surface (read-only everywhere). */
  getHistory?: (vars: { limit: number; offset: number }) => Promise<ReachHistoryPage>;
}

export interface ReachQueueViewProps {
  api: ReachApi;
  /** Distinct cache identity per surface/gym (e.g. 'self' or the gymId). */
  cacheKey: string;
  readOnly?: boolean;
  enabled?: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);

const daysAgo = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return '1d ago';
  return `${days}d ago`;
};

const fmtDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const fmtDateTime = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} · ${d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`;
};

const initials = (name: string) =>
  name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('') || '?';

type EffKind = 'active' | 'callback' | 'snoozed' | 'won' | 'dropped';

/** Snapshot state overridden by the live overlay (won/snoozes/drops made today). */
function effectiveState(entry: ReachEntry, overlay?: Record<string, OverlayState>): { kind: EffKind; label: string } {
  const o = overlay?.[entry.memberId];
  const state = o?.state;
  if (state === 'won') return { kind: 'won', label: 'Won 🎉' };
  if (state === 'dropped') return { kind: 'dropped', label: `Dropped${o?.droppedReason ? ` · ${o.droppedReason.replace(/_/g, ' ')}` : ''}` };
  const callbackAt = o !== undefined ? o.callbackAt : entry.callbackAt;
  const snoozeUntil = o !== undefined ? o.snoozeUntil : entry.snoozeUntil;
  if (callbackAt && new Date(callbackAt) <= new Date()) return { kind: 'callback', label: 'Call today 📞' };
  if (snoozeUntil && new Date(snoozeUntil) > new Date()) return { kind: 'snoozed', label: `Resting until ${fmtDate(snoozeUntil)}` };
  return { kind: 'active', label: '' };
}

const SOURCE_STYLE: Record<string, string> = {
  expired: 'bg-rose-500/10 text-rose-500 border-rose-200',
  trial: 'bg-amber-500/10 text-amber-600 border-amber-200',
  lead: 'bg-cyan-500/10 text-cyan-600 border-cyan-200'
};
const SOURCE_LABEL: Record<string, string> = { expired: 'Expired', trial: 'Trial ended', lead: 'Lead' };

const READ_ONLY_HINT = 'Read-only preview — disabled';

// ── Small building blocks ─────────────────────────────────────────────

function FunnelTimeline({ funnel, dark = false }: { funnel: FunnelInfo; dark?: boolean }) {
  const base = dark ? 'text-zinc-500' : 'text-zinc-300';
  const steps = [
    { label: 'Sent', done: !!funnel.sentAt || ['sent', 'delivered', 'read'].includes(funnel.state), at: funnel.sentAt, icon: <Check className="w-3.5 h-3.5" /> },
    { label: 'Delivered', done: !!funnel.deliveredAt || ['delivered', 'read'].includes(funnel.state), at: funnel.deliveredAt, icon: <CheckCheck className="w-3.5 h-3.5" /> },
    { label: 'Read', done: !!funnel.seenAt || funnel.state === 'read', at: funnel.seenAt, icon: <CheckCheck className="w-3.5 h-3.5" /> }
  ];

  if (funnel.state === 'none') {
    return (
      <div className={`flex items-center gap-2 text-xs font-bold uppercase tracking-widest ${base}`}>
        <MessageCircle className="w-4 h-4" /> Never messaged
      </div>
    );
  }
  if (funnel.state === 'failed') {
    return (
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-red-500">
        <X className="w-4 h-4" /> Message failed
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      {steps.map((s, i) => (
        <div key={s.label} className="flex items-center">
          {i > 0 && <div className={`w-6 h-px mx-1 ${s.done ? 'bg-emerald-400' : dark ? 'bg-zinc-700' : 'bg-zinc-200'}`} />}
          <div className="flex flex-col items-center gap-1" title={s.at ? new Date(s.at).toLocaleString('en-IN') : ''}>
            <div className={`w-7 h-7 rounded-full flex items-center justify-center border ${
              s.label === 'Read' && s.done
                ? dark ? 'bg-sky-500/20 text-sky-400 border-sky-400/40' : 'bg-sky-50 text-sky-600 border-sky-200'
                : s.done
                  ? dark ? 'bg-emerald-500/20 text-emerald-400 border-emerald-400/40' : 'bg-emerald-50 text-emerald-600 border-emerald-200'
                  : dark ? 'bg-zinc-800 text-zinc-600 border-zinc-700' : 'bg-zinc-50 text-zinc-300 border-zinc-200'
            }`}>
              {s.icon}
            </div>
            <span className={`text-[9px] font-black uppercase tracking-widest ${s.done ? (dark ? 'text-zinc-300' : 'text-zinc-600') : base}`}>
              {s.label}
            </span>
            <span className={`text-[9px] font-bold ${dark ? 'text-zinc-500' : 'text-zinc-400'}`}>{s.at ? daysAgo(s.at) : ''}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function ReasonChips({ reasons, limit, dark = false }: { reasons: string[]; limit?: number; dark?: boolean }) {
  const shown = limit ? reasons.slice(0, limit) : reasons;
  return (
    <div className="flex flex-wrap gap-1.5">
      {shown.map((r, i) => (
        <span
          key={i}
          className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider border ${
            dark ? 'bg-white/5 text-indigo-200 border-white/10' : 'bg-indigo-50 text-indigo-600 border-indigo-100'
          }`}
        >
          {r}
        </span>
      ))}
    </div>
  );
}

function ModalShell({ title, onClose, children, wide = false }: { title: React.ReactNode; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[2000] p-4 animate-in fade-in duration-200" {...backdropClose(onClose)}>
      <div
        className={`bg-white rounded-3xl shadow-2xl w-full ${wide ? 'max-w-2xl' : 'max-w-md'} max-h-[90vh] overflow-y-auto animate-in zoom-in duration-300`}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-zinc-100 sticky top-0 bg-white rounded-t-3xl z-10">
          <h3 className="text-lg font-black tracking-tight text-zinc-900 flex items-center gap-2">{title}</h3>
          <button onClick={onClose} className="p-2 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-full transition-all">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function ReadOnlyNote() {
  return (
    <p className="text-xs font-bold text-amber-600 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-2">
      <Eye className="w-4 h-4 flex-shrink-0" /> Read-only preview — you can walk the whole flow, but nothing is sent or saved.
    </p>
  );
}

// ── Detail panel (side rail on desktop, modal on small screens) ────────

function EmptyDetailPanel() {
  return (
    <div className="bg-white rounded-3xl border border-zinc-100 shadow-sm w-full text-center py-14 px-6">
      <Trophy className="w-10 h-10 text-emerald-400 mx-auto mb-4" />
      <p className="text-zinc-600 font-bold">Nothing needs action right now</p>
      <p className="text-zinc-400 text-xs font-medium mt-1">Pick anyone from the list to see their details.</p>
    </div>
  );
}

function FocusDetailPanel({ focus, focusEff, overlay, actionPending, readOnly, onSkip, setModal, onClose }: {
  focus: ReachEntry;
  focusEff: { kind: EffKind; label: string };
  overlay: Record<string, OverlayState>;
  actionPending: boolean;
  readOnly: boolean;
  onSkip: (entry: ReachEntry) => void;
  setModal: (m: { type: 'offer' | 'call' | 'drop' | 'resend' | 'soft'; entry: ReachEntry } | null) => void;
  onClose?: () => void;
}) {
  const isActionable = focusEff.kind === 'active' || focusEff.kind === 'callback';
  const locked = actionPending || readOnly || !isActionable;

  return (
    <div className="bg-white rounded-3xl border border-zinc-100 shadow-sm p-6 w-full">
      <div className="flex items-center justify-between mb-5 gap-2">
        <p className={`text-[10px] font-black uppercase tracking-[0.25em] ${focusEff.kind === 'callback' ? 'text-amber-600' : 'text-zinc-400'}`}>
          {focusEff.kind === 'callback' ? '📞 Callback due' : 'Selected'}
        </p>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="px-3 py-1 rounded-xl bg-zinc-100 text-[10px] font-black uppercase tracking-widest text-zinc-500 whitespace-nowrap">
            Rank #{focus.rank} · Score {focus.score}
          </span>
          {onClose && (
            <button onClick={onClose} className="p-1.5 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-full transition-all">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4 mb-5">
        <div className="w-14 h-14 rounded-2xl bg-zinc-900 text-white flex items-center justify-center text-xl font-black flex-shrink-0">
          {initials(focus.name)}
        </div>
        <div className="min-w-0">
          <h3 className="text-2xl font-black tracking-tight text-zinc-900 truncate">{focus.name}</h3>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            <span className={`px-2 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-wider border ${SOURCE_STYLE[focus.source]}`}>
              {SOURCE_LABEL[focus.source]}
            </span>
            {focus.optedOut && (
              <span className="px-2 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-wider bg-zinc-100 text-zinc-500 border border-zinc-200">
                Opted out — call only
              </span>
            )}
          </div>
          <p className="text-xs font-bold text-zinc-400 mt-1">{focus.phone}</p>
        </div>
      </div>

      <div className="rounded-2xl border border-zinc-100 divide-y divide-zinc-100 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5">
          <span className="text-[10px] font-black text-zinc-400 uppercase tracking-widest flex items-center gap-1.5"><IndianRupee className="w-3.5 h-3.5" />Lifetime spend</span>
          <span className="text-sm font-black text-zinc-900">{formatCurrency(focus.lifetimeSpend)}</span>
        </div>
        <div className="flex items-center justify-between px-4 py-2.5 gap-3">
          <span className="text-[10px] font-black text-zinc-400 uppercase tracking-widest flex-shrink-0">Last plan</span>
          <span className="text-sm font-black text-zinc-900 text-right truncate">
            {focus.lastPlan?.name ?? '—'}
            {focus.lastPlan?.endDate && <span className="block text-[10px] font-bold text-zinc-400 normal-case">ended {fmtDate(focus.lastPlan.endDate)}</span>}
          </span>
        </div>
        <div className="flex items-center justify-between px-4 py-2.5">
          <span className="text-[10px] font-black text-zinc-400 uppercase tracking-widest flex items-center gap-1.5"><Footprints className="w-3.5 h-3.5" />Last visit</span>
          <span className="text-sm font-black text-zinc-900">{daysAgo(focus.lastVisitAt)}</span>
        </div>
        <div className="flex items-center justify-between px-4 py-2.5">
          <span className="text-[10px] font-black text-zinc-400 uppercase tracking-widest flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />Touches</span>
          <span className="text-sm font-black text-zinc-900">{overlay[focus.memberId]?.actionCount ?? 0}</span>
        </div>
      </div>

      <div className="p-4 bg-zinc-50 rounded-2xl border border-zinc-100 mt-5">
        <p className="text-[9px] font-black text-zinc-400 uppercase tracking-widest mb-3">Payment reminder funnel</p>
        <FunnelTimeline funnel={focus.funnel} />
      </div>

      {!isActionable && (
        <p className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3 mt-5">
          {focusEff.label || 'Not actionable right now'} — actions are locked until this changes.
        </p>
      )}

      <div className="grid grid-cols-2 gap-2 mt-5">
        {!focus.optedOut && (
          <>
            <button
              onClick={() => setModal({ type: 'resend', entry: focus })}
              disabled={locked}
              title={readOnly ? READ_ONLY_HINT : !isActionable ? focusEff.label : undefined}
              className="px-3 py-2.5 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl text-xs font-black tracking-tight transition-all active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
            >
              <MessageCircle className="w-3.5 h-3.5" /> Resend reminder
            </button>
            <button
              onClick={() => setModal({ type: 'soft', entry: focus })}
              disabled={locked}
              title={readOnly ? READ_ONLY_HINT : !isActionable ? focusEff.label : undefined}
              className="px-3 py-2.5 bg-purple-500 hover:bg-purple-600 text-white rounded-xl text-xs font-black tracking-tight transition-all active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
            >
              <Sparkles className="w-3.5 h-3.5" /> Gentle nudge
            </button>
            <button
              onClick={() => setModal({ type: 'offer', entry: focus })}
              disabled={locked}
              title={!isActionable ? focusEff.label : undefined}
              className="px-3 py-2.5 bg-fuchsia-500 hover:bg-fuchsia-600 text-white rounded-xl text-xs font-black tracking-tight transition-all active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
            >
              <Gift className="w-3.5 h-3.5" /> Send offer
            </button>
          </>
        )}
        <button
          onClick={() => setModal({ type: 'call', entry: focus })}
          disabled={locked}
          title={!isActionable ? focusEff.label : undefined}
          className={`px-3 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-xs font-black tracking-tight transition-all active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 ${focus.optedOut ? 'col-span-2' : ''}`}
        >
          <PhoneCall className="w-3.5 h-3.5" /> Log a call
        </button>
        <button
          onClick={() => onSkip(focus)}
          disabled={locked}
          title={readOnly ? READ_ONLY_HINT : !isActionable ? focusEff.label : undefined}
          className="px-3 py-2.5 bg-zinc-100 hover:bg-zinc-200 rounded-xl text-xs font-black tracking-tight text-zinc-600 transition-all active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
        >
          <SkipForward className="w-3.5 h-3.5" /> Skip today
        </button>
        <button
          onClick={() => setModal({ type: 'drop', entry: focus })}
          disabled={locked}
          title={!isActionable ? focusEff.label : undefined}
          className="px-3 py-2.5 bg-zinc-100 hover:bg-red-50 rounded-xl text-xs font-black tracking-tight text-red-500 hover:text-red-600 transition-all active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
        >
          <UserX className="w-3.5 h-3.5" /> Drop
        </button>
      </div>
      {focus.optedOut && (
        <p className="text-[10px] font-bold text-zinc-400 leading-relaxed mt-4">
          This member opted out of WhatsApp — a call (or a chat at the front desk when they visit) is the way in.
        </p>
      )}
    </div>
  );
}

// ── Call modal ────────────────────────────────────────────────────────

const CALL_OUTCOMES = [
  { value: 'reached', label: 'Spoke to them' },
  { value: 'no_answer', label: 'No answer' },
  { value: 'busy', label: 'Busy / call later' },
  { value: 'wrong_number', label: 'Wrong number' }
];

function CallModal({ entry, onSubmit, onClose, pending, readOnly }: { entry: ReachEntry; onSubmit: (payload: any) => void; onClose: () => void; pending: boolean; readOnly: boolean }) {
  const [outcome, setOutcome] = useState('reached');
  const [notes, setNotes] = useState('');
  const [callbackAt, setCallbackAt] = useState('');

  return (
    <ModalShell title={<><PhoneCall className="w-5 h-5 text-emerald-500" /> Log call — {entry.name}</>} onClose={onClose}>
      <div className="space-y-5">
        <a href={`tel:${entry.phone}`} className="flex items-center justify-center gap-2 w-full py-3 bg-emerald-500 text-white rounded-2xl font-black tracking-tight hover:bg-emerald-600 transition-all active:scale-[0.98]">
          <PhoneCall className="w-4 h-4" /> Call {entry.phone}
        </a>
        <div>
          <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-2">How did it go?</p>
          <div className="grid grid-cols-2 gap-2">
            {CALL_OUTCOMES.map(o => (
              <button
                key={o.value}
                onClick={() => setOutcome(o.value)}
                className={`px-3 py-2.5 rounded-xl text-sm font-bold border transition-all ${
                  outcome === o.value ? 'bg-zinc-950 text-white border-zinc-950' : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-2">Notes (optional)</p>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={3}
            placeholder="e.g. Travelling this month, will decide after the 25th…"
            className="w-full border border-zinc-200 rounded-2xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div>
          <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-2">Remind me on (optional)</p>
          <input
            type="date"
            value={callbackAt}
            min={new Date().toISOString().split('T')[0]}
            onChange={e => setCallbackAt(e.target.value)}
            className="w-full border border-zinc-200 rounded-2xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <p className="text-[10px] text-zinc-400 font-bold mt-1.5">They'll pop to the top of the queue that day.</p>
        </div>
        {readOnly && <ReadOnlyNote />}
        <button
          onClick={() => onSubmit({ outcome, notes: notes || undefined, callbackAt: callbackAt || undefined })}
          disabled={pending || readOnly}
          title={readOnly ? READ_ONLY_HINT : undefined}
          className="w-full py-3.5 bg-zinc-950 text-white rounded-2xl font-black tracking-tight hover:bg-zinc-800 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {pending ? 'Saving…' : 'Save call log'}
        </button>
      </div>
    </ModalShell>
  );
}

// ── Drop modal ────────────────────────────────────────────────────────

const DROP_REASONS = [
  { value: 'price', label: 'Price' },
  { value: 'moved_away', label: 'Moved away' },
  { value: 'joined_another_gym', label: 'Joined another gym' },
  { value: 'injury_medical', label: 'Injury / medical' },
  { value: 'lost_interest', label: 'Lost interest' },
  { value: 'other', label: 'Other' }
];

function DropModal({ entry, onSubmit, onClose, pending, readOnly }: { entry: ReachEntry; onSubmit: (payload: any) => void; onClose: () => void; pending: boolean; readOnly: boolean }) {
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  return (
    <ModalShell title={<><UserX className="w-5 h-5 text-red-500" /> Drop {entry.name}</>} onClose={onClose}>
      <div className="space-y-5">
        <p className="text-sm text-zinc-500 font-medium">
          They'll leave the reachout queue until they start a new purchase cycle. Reasons feed your churn insights.
        </p>
        <div className="grid grid-cols-2 gap-2">
          {DROP_REASONS.map(r => (
            <button
              key={r.value}
              onClick={() => setReason(r.value)}
              className={`px-3 py-2.5 rounded-xl text-sm font-bold border transition-all ${
                reason === r.value ? 'bg-red-500 text-white border-red-500' : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={3}
          placeholder="Anything worth remembering… (optional)"
          className="w-full border border-zinc-200 rounded-2xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
        />
        {readOnly && <ReadOnlyNote />}
        <button
          onClick={() => onSubmit({ reason, notes: notes || undefined })}
          disabled={!reason || pending || readOnly}
          title={readOnly ? READ_ONLY_HINT : undefined}
          className="w-full py-3.5 bg-red-500 text-white rounded-2xl font-black tracking-tight hover:bg-red-600 transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {pending ? 'Dropping…' : 'Drop from queue'}
        </button>
      </div>
    </ModalShell>
  );
}

// ── Reminder preview (resend reminder / gentle nudge) ───────────────────

function ReminderPreviewModal({ entry, type, cacheKey, fetchContext, onSubmit, onClose, pending, readOnly }: {
  entry: ReachEntry;
  type: 'resend_reminder' | 'soft_reminder';
  cacheKey: string;
  fetchContext: (memberId: string) => Promise<MemberContext>;
  onSubmit: () => void;
  onClose: () => void;
  pending: boolean;
  readOnly: boolean;
}) {
  const isSoft = type === 'soft_reminder';

  const { data: ctx, isLoading } = useQuery<MemberContext>({
    queryKey: ['reach-context', cacheKey, entry.memberId],
    queryFn: () => fetchContext(entry.memberId)
  });

  const enabled = ctx ? (isSoft ? ctx.capabilities.softReminder : ctx.capabilities.resend) : false;
  const previewText = ctx ? (isSoft ? ctx.capabilities.softReminderPreview : ctx.capabilities.resendPreview) : null;
  const canSend = enabled && !!previewText;

  return (
    <ModalShell
      title={<>{isSoft ? <Sparkles className="w-5 h-5 text-purple-500" /> : <MessageCircle className="w-5 h-5 text-indigo-500" />} {isSoft ? 'Gentle nudge' : 'Resend reminder'} — {entry.name}</>}
      onClose={onClose}
    >
      {isLoading || !ctx ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-10 h-10 border-4 border-indigo-100 border-t-indigo-500 rounded-full animate-spin" />
        </div>
      ) : !canSend ? (
        <p className="text-sm text-zinc-500 font-medium py-4">
          {!enabled
            ? isSoft
              ? "The gentle nudge template isn't enabled for your gym yet — enable it from the template catalog."
              : 'The payment reminder template isn’t enabled for your gym yet.'
            : 'No past membership to remind about — try an offer or a call instead.'}
        </p>
      ) : (
        <div className="space-y-5">
          <div>
            <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-2">WhatsApp preview</p>
            <div className="bg-[#e5ddd5] rounded-2xl p-4">
              <div className="bg-white rounded-xl rounded-tl-none p-3 max-w-[85%] shadow-sm">
                <p className="text-sm text-zinc-800 whitespace-pre-wrap leading-relaxed">{formatWhatsAppText(previewText as string)}</p>
                <p className="text-[10px] text-zinc-400 text-right mt-1">
                  {new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          </div>
          {readOnly && <ReadOnlyNote />}
          <button
            onClick={onSubmit}
            disabled={pending || readOnly}
            title={readOnly ? READ_ONLY_HINT : undefined}
            className={`w-full py-3.5 text-white rounded-2xl font-black tracking-tight transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
              isSoft ? 'bg-purple-500 hover:bg-purple-600' : 'bg-indigo-500 hover:bg-indigo-600'
            }`}
          >
            <Send className="w-4 h-4" /> {pending ? 'Sending…' : 'Send on WhatsApp'}
          </button>
        </div>
      )}
    </ModalShell>
  );
}

// ── Offer wizard ──────────────────────────────────────────────────────

interface OfferIdea {
  kind: string;
  value?: number;
  label: string;
  detail?: string;
}

function OfferWizard({ entry, cacheKey, fetchContext, onSubmit, onClose, pending, readOnly }: {
  entry: ReachEntry;
  cacheKey: string;
  fetchContext: (memberId: string) => Promise<MemberContext>;
  onSubmit: (payload: any) => void;
  onClose: () => void;
  pending: boolean;
  readOnly: boolean;
}) {
  const [step, setStep] = useState(1);
  const [idea, setIdea] = useState<OfferIdea | null>(null);
  const [message, setMessage] = useState('');

  const { data: ctx, isLoading } = useQuery<MemberContext>({
    queryKey: ['reach-context', cacheKey, entry.memberId],
    queryFn: () => fetchContext(entry.memberId)
  });

  const lastPlan = ctx?.purchases?.[0];
  const planPrice = lastPlan?.planPrice ?? lastPlan?.amount ?? null;

  const ideas: OfferIdea[] = useMemo(() => {
    const list: OfferIdea[] = [];
    if (planPrice) {
      for (const pct of [10, 15, 20]) {
        const discounted = Math.round(planPrice * (1 - pct / 100));
        list.push({
          kind: 'discount',
          value: pct,
          label: `${pct}% off renewal`,
          detail: `${formatCurrency(discounted)} instead of ${formatCurrency(planPrice)}`
        });
      }
    }
    list.push(
      { kind: 'free_month', label: '+1 month free', detail: 'Extra month added on renewal' },
      { kind: 'pt_session', label: 'Free PT session', detail: 'One personal training session on us' },
      { kind: 'guest_passes', label: '2 guest passes', detail: 'Bring friends — they might join too' },
      { kind: 'custom', label: 'Custom message', detail: 'Write it yourself, no prefilled offer' }
    );
    return list;
  }, [planPrice]);

  const buildMessage = (i: OfferIdea) => {
    const name = ctx?.member.name || entry.name;
    const gym = ctx?.gymName || 'our gym';
    const plan = lastPlan?.planName || 'membership';
    if (i.kind === 'custom') {
      return `Hi ${name}! `;
    }
    if (i.kind === 'discount') {
      return `Hi ${name}! We'd love to have you back at ${gym} 💪 Renew your ${plan} this week and get *${i.value}% off* — ${i.detail}. Drop by or call the gym to claim it`;
    }
    return `Hi ${name}! We'd love to have you back at ${gym} 💪 Renew your ${plan} this week and get *${i.label}* (${i.detail}). Drop by or call the gym to claim it`;
  };

  const previewText = useMemo(() => {
    const template = ctx?.capabilities.offerTemplateContent || 'Update from *{{1}}*: {{2}}.\n\nThank you for your attention!';
    return template
      .replace(/\{\{1\}\}/g, ctx?.gymName || 'Gym')
      .replace(/\{\{2\}\}/g, message.trim().replace(/[.!?,;:]+$/, ''));
  }, [ctx, message]);

  return (
    <ModalShell
      wide
      onClose={onClose}
      title={<><Gift className="w-5 h-5 text-fuchsia-500" /> Win-back offer — {entry.name}
        <span className="ml-2 text-[10px] font-black text-zinc-400 uppercase tracking-widest">Step {step}/3</span></>}
    >
      {isLoading || !ctx ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-10 h-10 border-4 border-fuchsia-100 border-t-fuchsia-500 rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {step === 1 && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-4 bg-zinc-950 text-white rounded-2xl">
                  <p className="text-[9px] font-black text-indigo-300 uppercase tracking-widest mb-1">Lifetime</p>
                  <p className="text-lg font-black tracking-tight">{formatCurrency(ctx.totals.lifetimeSpend)}</p>
                </div>
                <div className="p-4 bg-zinc-50 rounded-2xl border border-zinc-100">
                  <p className="text-[9px] font-black text-zinc-400 uppercase tracking-widest mb-1">Memberships</p>
                  <p className="text-lg font-black tracking-tight text-zinc-900">{ctx.totals.membershipCount}</p>
                </div>
                <div className="p-4 bg-zinc-50 rounded-2xl border border-zinc-100">
                  <p className="text-[9px] font-black text-zinc-400 uppercase tracking-widest mb-1">Avg / month</p>
                  <p className="text-lg font-black tracking-tight text-zinc-900">{formatCurrency(ctx.totals.avgMonthlySpend)}</p>
                </div>
                <div className="p-4 bg-zinc-50 rounded-2xl border border-zinc-100">
                  <p className="text-[9px] font-black text-zinc-400 uppercase tracking-widest mb-1">Last visit</p>
                  <p className="text-lg font-black tracking-tight text-zinc-900">{daysAgo(ctx.lastVisitAt)}</p>
                </div>
              </div>
              <div>
                <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-2">Purchase history</p>
                <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                  {ctx.purchases.length === 0 && (
                    <p className="text-sm text-zinc-400 font-medium py-4 text-center">No purchases yet — this is a lead. A welcome offer works great here.</p>
                  )}
                  {ctx.purchases.map(p => (
                    <div key={p.id} className="flex items-center justify-between p-3 bg-white border border-zinc-100 rounded-xl">
                      <div>
                        <p className="text-sm font-bold text-zinc-800">{p.planName}</p>
                        <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">
                          {fmtDate(p.startDate)} → {fmtDate(p.endDate)}
                        </p>
                      </div>
                      <p className="text-sm font-black text-zinc-900">{formatCurrency(p.amount)}</p>
                    </div>
                  ))}
                </div>
              </div>
              <button
                onClick={() => setStep(2)}
                className="w-full py-3.5 bg-zinc-950 text-white rounded-2xl font-black tracking-tight hover:bg-zinc-800 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
              >
                Pick an offer <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <p className="text-sm text-zinc-500 font-medium">
                Ideas sized to their history — pick one, tweak the message next.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {ideas.map((i, idx) => (
                  <button
                    key={idx}
                    onClick={() => { setIdea(i); setMessage(buildMessage(i)); }}
                    className={`text-left p-4 rounded-2xl border transition-all ${
                      i.kind === 'custom' && idea?.label !== i.label ? 'border-dashed border-zinc-300 bg-white hover:border-zinc-400' :
                      idea?.label === i.label
                        ? 'border-fuchsia-500 bg-fuchsia-50 ring-2 ring-fuchsia-200'
                        : 'border-zinc-200 bg-white hover:border-zinc-400'
                    }`}
                  >
                    <p className="text-sm font-black tracking-tight text-zinc-900 flex items-center gap-1.5">
                      {i.kind === 'custom' && <Pencil className="w-3.5 h-3.5 text-zinc-400" />} {i.label}
                    </p>
                    {i.detail && <p className="text-xs font-bold text-zinc-500 mt-1">{i.detail}</p>}
                  </button>
                ))}
              </div>
              <div className="flex gap-3">
                <button onClick={() => setStep(1)} className="px-5 py-3 border border-zinc-200 rounded-2xl font-black text-zinc-600 hover:bg-zinc-50 transition-all flex items-center gap-1">
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
                <button
                  onClick={() => setStep(3)}
                  disabled={!idea}
                  className="flex-1 py-3 bg-zinc-950 text-white rounded-2xl font-black tracking-tight hover:bg-zinc-800 transition-all active:scale-[0.98] disabled:opacity-40 flex items-center justify-center gap-2"
                >
                  Compose message <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <div>
                <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-2">Your message ({message.length}/900)</p>
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  rows={4}
                  maxLength={900}
                  className="w-full border border-zinc-200 rounded-2xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-400"
                />
              </div>
              <div>
                <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-2">WhatsApp preview</p>
                <div className="bg-[#e5ddd5] rounded-2xl p-4">
                  <div className="bg-white rounded-xl rounded-tl-none p-3 max-w-[85%] shadow-sm">
                    <p className="text-sm text-zinc-800 whitespace-pre-wrap leading-relaxed">{formatWhatsAppText(previewText)}</p>
                    <p className="text-[10px] text-zinc-400 text-right mt-1">
                      {new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              </div>
              {!readOnly && !ctx.capabilities.offer && (
                <p className="text-xs font-bold text-amber-600 bg-amber-50 border border-amber-200 rounded-xl p-3">
                  The "Update from Gym" template isn't enabled for your gym yet — enable it from the template catalog to send offers.
                </p>
              )}
              {readOnly && <ReadOnlyNote />}
              <div className="flex gap-3">
                <button onClick={() => setStep(2)} className="px-5 py-3 border border-zinc-200 rounded-2xl font-black text-zinc-600 hover:bg-zinc-50 transition-all flex items-center gap-1">
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
                <button
                  onClick={() => onSubmit({ message: message.trim(), offer: idea ? { kind: idea.kind, value: idea.value ?? null, label: idea.label } : null })}
                  disabled={!message.trim() || pending || readOnly || !ctx.capabilities.offer}
                  title={readOnly ? READ_ONLY_HINT : undefined}
                  className="flex-1 py-3 bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white rounded-2xl font-black tracking-tight hover:opacity-90 transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Send className="w-4 h-4" /> {pending ? 'Sending…' : 'Send offer on WhatsApp'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </ModalShell>
  );
}

// ── History tab ──────────────────────────────────────────────────────

const ACTION_META: Record<string, { label: string; icon: React.ReactNode; badge: string }> = {
  resend_reminder: { label: 'Resent payment reminder', icon: <MessageCircle className="w-4 h-4" />, badge: 'bg-indigo-50 text-indigo-600' },
  soft_reminder: { label: 'Sent gentle nudge', icon: <Sparkles className="w-4 h-4" />, badge: 'bg-purple-50 text-purple-600' },
  offer: { label: 'Sent win-back offer', icon: <Gift className="w-4 h-4" />, badge: 'bg-fuchsia-50 text-fuchsia-600' },
  call: { label: 'Logged a call', icon: <PhoneCall className="w-4 h-4" />, badge: 'bg-emerald-50 text-emerald-600' },
  skip: { label: 'Skipped for today', icon: <SkipForward className="w-4 h-4" />, badge: 'bg-zinc-100 text-zinc-500' },
  drop: { label: 'Dropped from queue', icon: <UserX className="w-4 h-4" />, badge: 'bg-red-50 text-red-500' },
  won: { label: 'Renewed — marked won', icon: <Trophy className="w-4 h-4" />, badge: 'bg-emerald-50 text-emerald-600' }
};

const CALL_OUTCOME_LABEL: Record<string, string> = Object.fromEntries(CALL_OUTCOMES.map(o => [o.value, o.label]));
const DROP_REASON_LABEL: Record<string, string> = Object.fromEntries(DROP_REASONS.map(r => [r.value, r.label]));

function historyDetail(entry: ReachHistoryEntry): string | null {
  const p = entry.payload || {};
  switch (entry.type) {
    case 'call':
      return [CALL_OUTCOME_LABEL[p.outcome] || p.outcome, p.notes].filter(Boolean).join(' — ') || null;
    case 'drop':
      return [DROP_REASON_LABEL[p.reason] || p.reason, p.notes].filter(Boolean).join(' — ') || null;
    case 'resend_reminder':
    case 'soft_reminder':
    case 'offer':
      return p.messageText ? `"${p.messageText.slice(0, 120)}${p.messageText.length > 120 ? '…' : ''}"` : null;
    case 'skip':
      return 'Pushed to tomorrow';
    default:
      return null;
  }
}

function ReachHistoryTab({ getHistory }: { getHistory: (vars: { limit: number; offset: number }) => Promise<ReachHistoryPage> }) {
  const PAGE_SIZE = 30;
  const [entries, setEntries] = useState<ReachHistoryEntry[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getHistory({ limit: PAGE_SIZE, offset: 0 })
      .then(page => {
        if (cancelled) return;
        setEntries(page.entries);
        setHasMore(page.hasMore);
        setTotal(page.total);
      })
      .catch((e: any) => !cancelled && setError(e.message || 'Failed to load history'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [getHistory]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const page = await getHistory({ limit: PAGE_SIZE, offset: entries.length });
      setEntries(prev => [...prev, ...page.entries]);
      setHasMore(page.hasMore);
    } catch (e: any) {
      toast.error(e.message || 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-10 h-10 border-4 border-fuchsia-100 border-t-fuchsia-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return <p className="text-sm text-red-500 font-bold text-center py-16">{error}</p>;
  }

  if (entries.length === 0) {
    return (
      <div className="bg-white rounded-3xl border border-zinc-100 shadow-sm text-center py-16">
        <History className="w-10 h-10 text-zinc-300 mx-auto mb-4" />
        <p className="text-zinc-600 font-bold">No actions yet</p>
        <p className="text-zinc-400 text-xs font-medium mt-1">Everything you send, log or drop from the queue shows up here.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl border border-zinc-100 shadow-sm overflow-hidden">
      <div className="p-4 border-b border-zinc-100">
        <p className="text-xs font-bold text-zinc-400">{total ?? entries.length} action{(total ?? entries.length) === 1 ? '' : 's'} total</p>
      </div>
      <div className="divide-y divide-zinc-50">
        {entries.map(entry => {
          const meta = ACTION_META[entry.type] || { label: entry.type, icon: <Clock className="w-4 h-4" />, badge: 'bg-zinc-100 text-zinc-500' };
          const detail = historyDetail(entry);
          return (
            <div key={entry.id} className="px-4 py-3.5 flex items-start gap-4">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${meta.badge}`}>
                {meta.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-black tracking-tight text-zinc-900">{entry.memberName}</p>
                  {entry.memberPhone && <span className="text-xs font-bold text-zinc-400">{entry.memberPhone}</span>}
                </div>
                <p className="text-xs font-bold text-zinc-500 mt-0.5">{meta.label}</p>
                {detail && <p className="text-xs text-zinc-400 mt-1 italic truncate">{detail}</p>}
              </div>
              <div className="flex-shrink-0 text-right">
                <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">{fmtDateTime(entry.at)}</p>
                <p className="text-[10px] font-bold text-zinc-300 mt-0.5">{entry.performedBy || 'System'}</p>
              </div>
            </div>
          );
        })}
      </div>
      {hasMore && (
        <div className="p-4 border-t border-zinc-100 flex justify-center">
          <button
            onClick={loadMore}
            disabled={loadingMore}
            className="px-5 py-2.5 bg-zinc-50 hover:bg-zinc-100 rounded-xl text-xs font-black text-zinc-600 transition-all disabled:opacity-50"
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main view ─────────────────────────────────────────────────────────

type FilterKey = 'all' | 'hot' | 'leads' | 'optedOut' | 'resting' | 'done';

function matchesFilter(entry: ReachEntry, eff: { kind: EffKind; label: string }, key: FilterKey): boolean {
  switch (key) {
    case 'hot': return entry.funnel.state === 'read' && !entry.optedOut && (eff.kind === 'active' || eff.kind === 'callback');
    case 'leads': return entry.source === 'lead' || entry.source === 'trial';
    case 'optedOut': return entry.optedOut;
    case 'resting': return eff.kind === 'snoozed';
    case 'done': return eff.kind === 'won' || eff.kind === 'dropped';
    default: return true;
  }
}

export default function ReachQueueView({ api, cacheKey, readOnly = false, enabled = true }: ReachQueueViewProps) {
  const queryClient = useQueryClient();

  const [view, setView] = useState<'queue' | 'history'>('queue');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [focusId, setFocusId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [modal, setModal] = useState<{ type: 'offer' | 'call' | 'drop' | 'resend' | 'soft'; entry: ReachEntry } | null>(null);

  const todayKey = ['reach-today', cacheKey];

  const { data, isLoading } = useQuery<TodayData>({
    queryKey: todayKey,
    queryFn: api.getToday,
    enabled,
    staleTime: 60 * 1000,
    // A super-admin preview lives only in the cache — a background refetch
    // would clobber it with { exists: false }.
    refetchOnWindowFocus: false
  });

  const unlock = useMutation({
    mutationFn: api.unlock,
    onSuccess: fresh => {
      queryClient.setQueryData(todayKey, fresh);
      toast.success(readOnly ? 'Preview generated — nothing was saved' : "Today's queue is ready 🔥");
    },
    onError: (e: any) => toast.error(e.message || 'Failed to generate analysis')
  });

  const action = useMutation({
    mutationFn: async (vars: { memberId: string; type: string; payload?: any }) => {
      if (!api.performAction) throw new Error(READ_ONLY_HINT);
      return api.performAction(vars);
    },
    onSuccess: (_res, vars) => {
      queryClient.invalidateQueries({ queryKey: todayKey });
      queryClient.invalidateQueries({ queryKey: ['reach-context', cacheKey, vars.memberId] });
      setModal(null);
      const messages: Record<string, string> = {
        resend_reminder: 'Reminder sent — moved to the tail for 3 days',
        soft_reminder: 'Gentle nudge sent — moved to the tail for 3 days',
        offer: 'Offer sent on WhatsApp 🎁',
        call: 'Call logged',
        skip: 'Skipped to tomorrow',
        drop: 'Dropped from the queue'
      };
      toast.success(messages[vars.type] || 'Done');
    },
    onError: (e: any, vars) => {
      const msg = e?.message || 'Action failed';
      if (msg.toLowerCase().includes('renewed')) {
        toast.success(msg);
        queryClient.invalidateQueries({ queryKey: todayKey });
        setModal(null);
      } else {
        toast.error(msg);
      }
      void vars;
    }
  });

  const entries = data?.entries || [];
  const overlay = data?.overlay || {};
  const isPreview = !!data?.preview;

  const filtered = useMemo(
    () => entries.filter(e => matchesFilter(e, effectiveState(e, overlay), filter)),
    [entries, overlay, filter]
  );

  const filterCounts = useMemo(() => {
    const keys: FilterKey[] = ['hot', 'leads', 'optedOut', 'resting', 'done'];
    const counts = { all: entries.length } as Record<FilterKey, number>;
    for (const key of keys) {
      counts[key] = entries.filter(e => matchesFilter(e, effectiveState(e, overlay), key)).length;
    }
    return counts;
  }, [entries, overlay]);

  // Focus: explicit selection, else first actionable entry (callbacks first —
  // entries are already snapshot-ranked with callbacks pinned).
  const focus = useMemo(() => {
    const actionable = (e: ReachEntry) => ['active', 'callback'].includes(effectiveState(e, overlay).kind);
    if (focusId) {
      const chosen = entries.find(e => e.memberId === focusId);
      if (chosen) return chosen;
    }
    return entries.find(actionable) || null;
  }, [entries, overlay, focusId]);

  const run = (type: string, entry: ReachEntry, payload?: any) => {
    if (readOnly) return;
    action.mutate({ memberId: entry.memberId, type, payload });
  };

  // Skip moves the focus straight to the next actionable member so the
  // manager can keep working the queue without an extra click.
  const skipToNext = (entry: ReachEntry) => {
    const isActionable = (e: ReachEntry) => e.memberId !== entry.memberId && ['active', 'callback'].includes(effectiveState(e, overlay).kind);
    const idx = entries.findIndex(e => e.memberId === entry.memberId);
    const next = entries.slice(idx + 1).find(isActionable) || entries.find(isActionable) || null;
    setFocusId(next?.memberId ?? null);
    run('skip', entry);
  };

  // ── Render: loading ─────────────────────────────────────────────────
  if (isLoading || !enabled) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-fuchsia-100 border-t-fuchsia-600 rounded-full animate-spin" />
          <Radar className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 text-fuchsia-600 animate-pulse" />
        </div>
      </div>
    );
  }

  // ── Render: locked (pre-unlock ritual / feature teaser) ────────────
  // History is independent of today's unlock state, so it bypasses the teaser.
  if (!data?.exists && view !== 'history') {
    // Plan doesn't include reach_ai: show the tab as a teaser — everything
    // visible, generate button locked. Server refuses unlock/actions anyway.
    const featureLocked = !readOnly && data?.featureEnabled === false;
    return (
      <div className="flex items-center justify-center min-h-[70vh] animate-in fade-in duration-700">
        <div className="ai-dark-card ai-glow-fuchsia max-w-xl w-full text-center py-14 px-8">
          <div className="absolute top-0 right-0 p-6 opacity-10">
            <Radar className="w-40 h-40 text-fuchsia-500" />
          </div>
          <div className="relative z-10">
            <div className="w-20 h-20 mx-auto rounded-3xl bg-fuchsia-500/20 flex items-center justify-center mb-6">
              {featureLocked
                ? <Lock className="w-10 h-10 text-fuchsia-400" />
                : <Radar className={`w-10 h-10 text-fuchsia-400 ${unlock.isLoading ? 'animate-spin' : 'animate-pulse'}`} />}
            </div>
            <h1 className="text-4xl font-black tracking-tighter mb-3 flex items-center justify-center gap-3">
              <span className="ai-gradient-text">REACH AI</span>
              {featureLocked && (
                <span className="px-2.5 py-1 rounded-xl bg-white/10 text-zinc-300 text-[10px] font-black uppercase tracking-widest border border-white/10">
                  Not in your plan
                </span>
              )}
            </h1>
            <p className="text-zinc-400 font-bold uppercase tracking-widest text-xs mb-2">
              {readOnly ? 'Read-only preview · Super admin' : 'Follow-up intelligence · Renewals & Leads'}
            </p>
            <p className="text-zinc-300 text-sm font-medium max-w-md mx-auto mb-8 leading-relaxed">
              {unlock.isLoading
                ? 'Reading reminder funnels, purchase history and visit patterns…'
                : featureLocked
                  ? 'Reach AI ranks every expired member, lapsed trial and lead — by who read your payment reminders, what they\'ve spent with you, and who\'s still walking in — then gives you one-tap follow-ups: resend, offers, calls. Contact support to add it to your plan.'
                  : readOnly
                    ? "The gym hasn't unlocked today's queue yet. Generate an ephemeral preview — same derivation and ranking, but nothing is saved and nothing can be sent."
                    : "Today's analysis is ready. Unlock to rank everyone worth reaching out to — by who read your reminders, what they've spent, and who's still walking in."}
            </p>
            <button
              onClick={() => unlock.mutate()}
              disabled={unlock.isLoading || featureLocked}
              title={featureLocked ? 'Reach AI is not part of your current plan' : undefined}
              className="px-10 py-4 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white rounded-2xl font-black tracking-tight text-lg hover:opacity-90 transition-all active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-3"
            >
              {unlock.isLoading
                ? <><RefreshCw className="w-5 h-5 animate-spin" /> Analyzing members…</>
                : featureLocked
                  ? <><Lock className="w-5 h-5" /> Unlock with an upgrade</>
                  : readOnly
                    ? <><Eye className="w-5 h-5" /> Preview today's queue</>
                    : <><Sparkles className="w-5 h-5" /> Unlock today's queue</>}
            </button>
            <p className="text-zinc-500 text-[10px] font-black uppercase tracking-widest mt-6">
              {featureLocked
                ? 'Available as a plan add-on · Ask us to enable it'
                : readOnly
                  ? 'Not persisted · No messages can be sent'
                  : 'Computed once a day · A renewed member never appears'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const focusEff = focus ? effectiveState(focus, overlay) : null;

  // ── Render: queue ───────────────────────────────────────────────────
  return (
    <div className="space-y-6 pb-12 animate-in fade-in duration-700">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tighter flex items-center gap-3">
            <span className="ai-gradient-text">REACH AI</span>
            <div className="p-2 bg-fuchsia-100 rounded-2xl">
              <Radar className="w-6 h-6 text-fuchsia-600" />
            </div>
            {(readOnly || isPreview) && (
              <span className="px-3 py-1.5 rounded-xl bg-amber-100 text-amber-700 text-[10px] font-black uppercase tracking-widest border border-amber-200 flex items-center gap-1.5">
                <Eye className="w-3.5 h-3.5" /> Read-only {isPreview ? 'preview' : ''}
              </span>
            )}
          </h1>
          <p className="text-zinc-500 font-bold uppercase tracking-widest text-xs mt-2">
            {view === 'history'
              ? 'Full history of actions taken from the Reach AI queue'
              : isPreview
                ? `Ephemeral preview · generated ${data?.generatedAt ? new Date(data.generatedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'} · not saved`
                : `Today's reachout queue · frozen at ${data?.generatedAt ? new Date(data.generatedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'} · next analysis tomorrow`}
          </p>
        </div>
        {view === 'queue' && readOnly && isPreview && (
          <button
            onClick={() => unlock.mutate()}
            disabled={unlock.isLoading}
            className="px-4 py-2.5 bg-white border border-zinc-200 rounded-2xl text-xs font-black text-zinc-600 hover:bg-zinc-50 transition-all flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${unlock.isLoading ? 'animate-spin' : ''}`} /> Regenerate preview
          </button>
        )}
      </div>

      {/* Tabs */}
      {api.getHistory && (
        <div className="flex items-center gap-2">
          <button
            onClick={() => setView('queue')}
            className={`px-4 py-2 rounded-xl text-xs font-black tracking-tight transition-all flex items-center gap-1.5 ${
              view === 'queue' ? 'bg-zinc-950 text-white' : 'bg-zinc-50 text-zinc-500 hover:bg-zinc-100'
            }`}
          >
            <Radar className="w-3.5 h-3.5" /> Queue
          </button>
          <button
            onClick={() => setView('history')}
            className={`px-4 py-2 rounded-xl text-xs font-black tracking-tight transition-all flex items-center gap-1.5 ${
              view === 'history' ? 'bg-zinc-950 text-white' : 'bg-zinc-50 text-zinc-500 hover:bg-zinc-100'
            }`}
          >
            <History className="w-3.5 h-3.5" /> History
          </button>
        </div>
      )}

      {view === 'history' && api.getHistory && <ReachHistoryTab getHistory={api.getHistory} />}

      {/* Empty queue */}
      {view === 'queue' && entries.length === 0 && (
        <div className="ai-dark-card ai-glow-indigo text-center py-16">
          <Trophy className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
          <h3 className="text-2xl font-black tracking-tight mb-2">Queue clear 🎉</h3>
          <p className="text-zinc-400 font-medium">Everyone's covered by a plan. Check back tomorrow.</p>
        </div>
      )}

      {/* List + detail — side by side on desktop so the list is always
          fully visible; on small screens the detail opens as a modal. */}
      {view === 'queue' && entries.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-6 items-start">
          <div className="bg-white rounded-3xl border border-zinc-100 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-zinc-100 flex items-center gap-2 flex-wrap">
              {([
                ['all', 'All'],
                ['hot', '🔥 Hot'],
                ['leads', 'Leads & trials'],
                ['optedOut', 'Opted out'],
                ['resting', 'Resting'],
                ['done', 'Done']
              ] as [FilterKey, string][]).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setFilter(key)}
                  className={`px-4 py-2 rounded-xl text-xs font-black tracking-tight transition-all ${
                    filter === key ? 'bg-zinc-950 text-white' : 'bg-zinc-50 text-zinc-500 hover:bg-zinc-100'
                  }`}
                >
                  {label} ({filterCounts[key]})
                </button>
              ))}
            </div>

            <div className="divide-y divide-zinc-50 max-h-[75vh] overflow-y-auto overscroll-contain">
              {filtered.length === 0 && (
                <p className="text-sm text-zinc-400 font-medium text-center py-10">Nothing here right now.</p>
              )}
              {filtered.map(entry => {
                const eff = effectiveState(entry, overlay);
                const isFocus = focus?.memberId === entry.memberId;
                const muted = eff.kind === 'snoozed' || eff.kind === 'dropped';
                return (
                  <button
                    key={entry.memberId}
                    onClick={() => { setFocusId(entry.memberId); setDetailOpen(true); }}
                    className={`w-full text-left px-4 py-3.5 flex items-center gap-4 transition-all hover:bg-zinc-50 ${
                      isFocus ? 'bg-fuchsia-50/60' : ''
                    } ${muted ? 'opacity-50' : ''}`}
                  >
                    <span className="text-xs font-black text-zinc-300 w-7 flex-shrink-0">#{entry.rank}</span>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-black text-white flex-shrink-0 ${
                      eff.kind === 'won' ? 'bg-emerald-500' : 'bg-zinc-900'
                    }`}>
                      {eff.kind === 'won' ? <Trophy className="w-4 h-4" /> : initials(entry.name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-black tracking-tight text-zinc-900 truncate">{entry.name}</p>
                        <span className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider border ${SOURCE_STYLE[entry.source]}`}>
                          {SOURCE_LABEL[entry.source]}
                        </span>
                        {entry.optedOut && <Ban className="w-3.5 h-3.5 text-zinc-300" />}
                        {eff.label && (
                          <span className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider ${
                            eff.kind === 'won' ? 'bg-emerald-50 text-emerald-600'
                            : eff.kind === 'callback' ? 'bg-amber-50 text-amber-600'
                            : 'bg-zinc-100 text-zinc-500'
                          }`}>
                            {eff.label}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 hidden sm:block">
                        <ReasonChips reasons={entry.reasons} limit={3} />
                      </div>
                    </div>
                    <div className="hidden md:flex items-center gap-1.5 flex-shrink-0" title={`Funnel: ${entry.funnel.state}`}>
                      {entry.funnel.state === 'read' && <CheckCheck className="w-4 h-4 text-sky-500" />}
                      {entry.funnel.state === 'delivered' && <CheckCheck className="w-4 h-4 text-zinc-400" />}
                      {entry.funnel.state === 'sent' && <Check className="w-4 h-4 text-zinc-400" />}
                      {entry.funnel.state === 'failed' && <X className="w-4 h-4 text-red-400" />}
                      <span className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">{entry.funnel.state}</span>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <p className="text-sm font-black tracking-tight text-zinc-900">{entry.score}</p>
                      <p className="text-[9px] font-black text-zinc-300 uppercase tracking-widest">score</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-200 flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Detail: a normal side column on lg+ screens (nothing ever
              overlaps the list); a centered modal on smaller screens,
              opened explicitly by tapping a row. */}
          <div
            onClick={() => detailOpen && setDetailOpen(false)}
            className={
              detailOpen
                ? 'fixed inset-0 z-[2000] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 lg:static lg:inset-auto lg:z-auto lg:bg-transparent lg:backdrop-blur-none lg:p-0 lg:flex lg:items-start lg:justify-start lg:sticky lg:top-6'
                : 'hidden lg:flex lg:items-start lg:justify-start lg:sticky lg:top-6'
            }
          >
            <div
              onClick={e => e.stopPropagation()}
              className="w-full max-w-md max-h-[90vh] overflow-y-auto lg:max-w-none lg:max-h-none lg:overflow-visible"
            >
              {focus && focusEff ? (
                <FocusDetailPanel
                  focus={focus}
                  focusEff={focusEff}
                  overlay={overlay}
                  actionPending={action.isLoading}
                  readOnly={readOnly}
                  onSkip={skipToNext}
                  setModal={setModal}
                  onClose={detailOpen ? () => setDetailOpen(false) : undefined}
                />
              ) : (
                <EmptyDetailPanel />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {modal?.type === 'call' && (
        <CallModal
          entry={modal.entry}
          pending={action.isLoading}
          readOnly={readOnly}
          onClose={() => setModal(null)}
          onSubmit={payload => run('call', modal.entry, payload)}
        />
      )}
      {modal?.type === 'drop' && (
        <DropModal
          entry={modal.entry}
          pending={action.isLoading}
          readOnly={readOnly}
          onClose={() => setModal(null)}
          onSubmit={payload => run('drop', modal.entry, payload)}
        />
      )}
      {modal?.type === 'offer' && (
        <OfferWizard
          entry={modal.entry}
          cacheKey={cacheKey}
          fetchContext={api.getMemberContext}
          pending={action.isLoading}
          readOnly={readOnly}
          onClose={() => setModal(null)}
          onSubmit={payload => run('offer', modal.entry, payload)}
        />
      )}
      {modal?.type === 'resend' && (
        <ReminderPreviewModal
          entry={modal.entry}
          type="resend_reminder"
          cacheKey={cacheKey}
          fetchContext={api.getMemberContext}
          pending={action.isLoading}
          readOnly={readOnly}
          onClose={() => setModal(null)}
          onSubmit={() => run('resend_reminder', modal.entry)}
        />
      )}
      {modal?.type === 'soft' && (
        <ReminderPreviewModal
          entry={modal.entry}
          type="soft_reminder"
          cacheKey={cacheKey}
          fetchContext={api.getMemberContext}
          pending={action.isLoading}
          readOnly={readOnly}
          onClose={() => setModal(null)}
          onSubmit={() => run('soft_reminder', modal.entry)}
        />
      )}
    </div>
  );
}
